import { Button, Card, Space, Tag, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Grid as ZhGrid } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import Permission from "@/views/components/Permission";
import { useUserStore } from "@/stores/react/authStore";
import type { AiCorpusPublishRow } from "./AiCorpusPublishPage";

export interface AiCorpusPublishDetailPageProps {
    loadDetail?: (id: string) => Promise<AiCorpusPublishRow>;
    publish?: (id: number) => Promise<unknown>;
    remove?: (id: number) => Promise<unknown>;
    revert?: (id: number) => Promise<unknown>;
}

/** 复用 Vue AI语料发布详情接口。 */
export async function loadAiCorpusPublishDetail(id: string): Promise<AiCorpusPublishRow> {
    const response = await request.service.get<AiCorpusPublishRow>(`/corpus-publish/vo/${id}`);
    return response.data;
}

function isDraft(detail: AiCorpusPublishRow): boolean {
    return Number(detail.corpusPublish.state) === 1;
}

function formatDate(value?: string, includeTime = false): string {
    if (!value) return DEFAULT_VALUE;
    return value.replace("T", includeTime ? " " : "").slice(0, includeTime ? 19 : 10);
}

/** AI语料发布详情，保留 Vue 的只读字段、状态操作和发布数量边界。 */
export function AiCorpusPublishDetailPage({
    loadDetail = loadAiCorpusPublishDetail,
    publish,
    remove,
    revert
}: AiCorpusPublishDetailPageProps) {
    const navigate = useNavigate();
    const banButton = useUserStore(state => state.banButton);
    const { id = "" } = useParams<{ id: string }>();
    const [detail, setDetail] = useState<AiCorpusPublishRow>({ corpusPublish: { id: "", state: 1 }, corpusList: [] });
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [warningMessage, setWarningMessage] = useState<string | null>(null);

    useEffect(() => {
        let disposed = false;
        void loadDetail(id)
            .then(result => {
                if (!disposed) setDetail(result);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("AI语料发布详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [id, loadDetail]);

    const runPublish = async () => {
        if (detail.corpusList.length < 10) {
            setWarningMessage("至少发布10条语料");
            return;
        }
        if (publish) await publish(Number(detail.corpusPublish.id));
        setWarningMessage(null);
    };

    const runAction = async (action?: (id: number) => Promise<unknown>) => {
        if (action) await action(Number(detail.corpusPublish.id));
    };

    return (
        <section className="flex h-full min-h-full flex-col" aria-label="ai-corpus-publish-detail-page">
            <Card>
                <Typography.Title level={4}>AI语料发布详情</Typography.Title>
                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
                {warningMessage ? (
                    <Alert className="mb-4" message={warningMessage} role="alert" type="warning" />
                ) : null}
                <Space className="mb-4" orientation="vertical" size="small">
                    <span>
                        状态：<Tag>{isDraft(detail) ? "草稿" : "已发布"}</Tag>
                    </span>
                    <span>创建时间：{formatDate(detail.corpusPublish.createdTime, true)}</span>
                    <span>日期：{formatDate(detail.corpusPublish.updatedTime)}</span>
                </Space>
                <Typography.Title level={5}>语料内容</Typography.Title>
                <ZhGrid gap={[20, 20]} cols={4} style={{ gridTemplateColumns: "repeat(4, minmax(280px, 1fr))" }}>
                    {detail.corpusList.map((corpus, index) => (
                        <div key={String(corpus.id ?? index)}>
                            <Typography.Text type="secondary">{`语料${index + 1}：`}</Typography.Text>
                            <span>{(corpus as { content?: string }).content || DEFAULT_VALUE}</span>
                        </div>
                    ))}
                </ZhGrid>
                <Space className="mt-6">
                    {isDraft(detail) ? (
                        <>
                            <Permission anyOf={["ROLE_ASSISTANTS_CORPUS_W"]}>
                                <Button
                                    aria-label={`发布 ${detail.corpusPublish.id}`}
                                    disabled={banButton}
                                    type="primary"
                                    onClick={() => void runPublish()}
                                >
                                    发布
                                </Button>
                            </Permission>
                            <Permission anyOf={["ROLE_ASSISTANTS_CORPUS_W"]}>
                                <Button
                                    aria-label={`编辑 ${detail.corpusPublish.id}`}
                                    disabled={banButton}
                                    onClick={() =>
                                        void navigate(
                                            `/intelligentAssistant/aiCorpus/publish/edit/${detail.corpusPublish.id}`
                                        )
                                    }
                                >
                                    编辑
                                </Button>
                            </Permission>
                            <Permission anyOf={["ROLE_ASSISTANTS_CORPUS_W"]}>
                                <Button
                                    aria-label={`删除 ${detail.corpusPublish.id}`}
                                    disabled={banButton}
                                    onClick={() => void runAction(remove)}
                                >
                                    删除
                                </Button>
                            </Permission>
                        </>
                    ) : (
                        <Permission anyOf={["ROLE_ASSISTANTS_CORPUS_W"]}>
                            <Button
                                aria-label={`撤回 ${detail.corpusPublish.id}`}
                                disabled={banButton}
                                type="primary"
                                onClick={() => void runAction(revert)}
                            >
                                撤回
                            </Button>
                        </Permission>
                    )}
                    <Permission anyOf={["ROLE_ASSISTANTS_CORPUS_W"]}>
                        <Button
                            aria-label="返回"
                            disabled={banButton}
                            onClick={() => void navigate("/intelligentAssistant/aiCorpus/publish")}
                        >
                            返回
                        </Button>
                    </Permission>
                </Space>
            </Card>
        </section>
    );
}
